import { useEffect, useState } from "react"
import { getRandomText } from "../shared/utils"
import Link from "next/link"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"
import Image from "next/image"

export function HeaderImage({ image, alt, texts, country, city, title }: { image: string, alt: string, texts?: string[], country?: string, city?: string, title?: string }) {
    const [randomText, setRandomText] = useState("")
    const [pathname, setPathname] = useState<string[]>([])
    const { t } = useTranslation()
    const router = useRouter()
    const { locale } = router.locale ? router : { locale: 'it' }

    useEffect(() => {
        const paths = router.asPath.split('/')
        setPathname(paths)
    
        if (texts) {
            setRandomText(t(getRandomText(texts)))
        }
    }, [router.asPath, texts, locale, t])

    const scrollToElement = (elementId: string) => {
        const element = document.getElementById(elementId)
        if (element) {
            const distance = element.getBoundingClientRect().top
            window.scrollTo({
                top: window.scrollY + distance,
                behavior: "smooth"
            })
        }
    }

    return (
        <div id="header-image" className="relative font-poppins bg-white max-w-[80rem] mx-auto">
            {/* Image Container */}
            <div className="relative h-[60vh] lg:h-[60vh] w-full">
                <Image
                    src={image}
                    alt={alt}
                    priority={true}
                    className="xl-m:rounded-xl xl-m:shadow-lg bg-primary bg-opacity-50 object-cover object-center"
                    fill
                    quality={50}
                    sizes="(max-width: 480px) 100vw, 
                        (max-width: 768px) 100vw, 
                        (max-width: 1024px) 75vw, 
                        (max-width: 1280px) 50vw, 
                        50vw"
                />
            </div>
            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-20 xl-m:rounded-xl"></div>
            {/* Random Text */}
            {texts && (
                <div className="absolute top-5 left-0 right-0 text-center mx-auto">
                    <p className="animate-slidein text-white text-xl sm:text-2xl md:text-4xl">{randomText}</p>
                </div>
            )}
            {/* Conditional Arrow */}
            {(pathname[pathname.length - 2] === "" || pathname[1] === "destinations") && (
                <div className="absolute bottom-5 right-1/2 transform translate-x-1/2 mx-auto text-center cursor-pointer">
                    <img className="h-8 shadow-black" src="/assets/arrows.png" alt="arrow" onClick={() => scrollToElement("title")} />
                </div>
            )}
            {/* Breadcrumb Navigation */}
            {pathname[pathname.length - 2] === "country" && country !== undefined && (
                <div className="absolute bottom-5 right-1/2 transform translate-x-1/2 mx-auto text-center text-sm font-poppins text-white">
                    <div className="flex flex-wrap xm:flex-nowrap xm:flex-row justify-center gap-1">
                        <div className="flex flex-row gap-1">
                            <Link href="/" className="text-white">
                                {"Home"}
                            </Link>
                            <span>{">"}</span>
                            <Link href="/destinations" className="text-white">
                                {t("common.destinations")}
                            </Link>
                            <span>{">"}</span>
                        </div>
                        <div className="text-center">
                            <span>
                                {country?.charAt(0).toUpperCase() + country?.slice(1)}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            {pathname[pathname.length - 2] === "post" && country !== undefined && (
                <div className="absolute bottom-5 right-1/2 transform translate-x-1/2 text-center text-sm font-poppins text-white w-[90%]">
                    <div className="flex flex-col gap-1 md:gap-3 xl:gap-5">
                        <div className="flex flex-wrap xm:flex-nowrap xm:flex-row justify-center gap-1">
                            <div className="flex flex-row gap-1">
                                <Link href="/" className="text-white">
                                    {"Home"}
                                </Link>
                                <span>{">"}</span>
                                <Link href="/destinations" className="text-white">
                                    {t("common.destinations")}
                                </Link>
                                <span>{">"}</span>
                            </div>
                            <div className="text-center">
                                <span>
                                    {country?.charAt(0).toUpperCase() + country?.slice(1)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {pathname[pathname.length - 2] === "city" && city !== undefined && country !== undefined && (
                <div className="absolute bottom-5 right-1/2 transform translate-x-1/2 mx-auto text-center">
                    <span className="flex flex-row gap-1 text-sm font-poppins text-white">
                        <Link href="/" className="text-white">
                            {"Home"}
                        </Link>
                        <p>{">"}</p>
                        <Link href="/destinations" className="text-white">
                            {t("common.destinations")}
                        </Link>
                        <p>{">"}</p>
                        <Link href={{ pathname: "/country/[name]", query: { name: country } }} className="text-white">
                            {country?.charAt(0).toUpperCase() + country?.slice(1)}
                        </Link>
                        <p>{">"}</p>
                        {city?.charAt(0).toUpperCase() + city?.slice(1)}
                    </span>
                </div>
            )}
        </div>
    )
}